/**
 * `<ExportClientWriteupButton>` — the single candidate-detail action that
 * generates a `candidate_writeup` export, previews the branded PDF in-app, and
 * offers a one-click download with a meaningful filename.
 *
 * This is the JD-5 demo beat: reviewing a candidate → one click → branded
 * write-up → download.
 *
 * ── State machine (the button reflects the export job) ────────────────────────
 * Write-up generation is an async export job (`exports` table:
 * `pending → running → complete`, 7-day artifact retention). The button
 * discovers the candidate's most-recent non-expired `candidate_writeup` export
 * from the polled `useExports()` list and reflects it:
 *
 *   idle       (none / failed / expired) → click POSTs a new export + opens the
 *                                          modal with a live generating spinner.
 *   generating (pending / running)       → shows an inline spinner; click opens
 *                                          the modal to WATCH progress. Closing
 *                                          the modal does NOT cancel the job —
 *                                          the button stays "Generating…" and a
 *                                          toast fires when it completes.
 *   ready      (complete)                → click downloads the ALREADY-GENERATED
 *                                          artifact INSTANTLY (no modal, no
 *                                          regeneration). The write-up is cached
 *                                          server-side keyed by candidate; we
 *                                          reuse it rather than rebuild it.
 *
 * So: close-and-return works (the job survives a modal close and the button
 * tracks it), and a second click is an instant re-download — never a re-run.
 * An explicit "Regenerate" in the modal is the escape hatch when the underlying
 * data or branding changed and a fresh build is wanted.
 *
 * Why in-app preview + an authenticated blob save (not a bare `<a href>`)?
 * The export download endpoint is Bearer-only; a plain anchor navigation can't
 * carry the token, so the PDF fetch goes through `apiClient` (Bearer injected)
 * as a blob. The download then writes that already-fetched blob to disk via the
 * shared `saveBlobAsFile` / `downloadExport` helpers — no viewer redirect.
 *
 * Filename: `{CandidateName}_{Role}_WriteUp.pdf` (slugified, no spaces) — kept in
 * sync with the backend `Content-Disposition` so the saved copy reads the same.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from "react";

import {
  useCreateExport,
  useExport,
  useExports,
} from "@/hooks/queries/admin/useExports";
import { downloadExport, saveBlobAsFile } from "@/lib/downloadExport";
import { useToastStore } from "@/stores/useToastStore";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/common/Spinner";
import { WriteupPreview } from "@/components/candidate-page/WriteupPreview";
import { useParams } from "react-router-dom";

interface ExportClientWriteupButtonProps {
  /** Candidate UUID the write-up is generated for. */
  candidateId: string;
  /** Candidate display name — drives the saved filename. */
  candidateName: string;
  /** Role / JD title the candidate is submitted for (null when no JD context). */
  role?: string | null;
  /** Optional extra classes for the trigger button (e.g. `w-full` in the v2 hero). */
  className?: string;
}

/** Statuses that mean "an artifact is ready to hand to a client, right now". */
const READY = "complete";
/** Statuses that mean "generation is in flight". */
const IN_FLIGHT = new Set(["pending", "running"]);

/**
 * Collapse a free-text label into a single CamelCase-ish filename token with no
 * spaces or punctuation — mirrors the backend `_filename_token`. "Jane Doe" →
 * "JaneDoe", "Senior Engineer" → "SeniorEngineer". Empty → `fallback`.
 */
function filenameToken(label: string | null | undefined, fallback: string): string {
  if (!label) return fallback;
  const words = label.normalize("NFKD").replace(/[^A-Za-z0-9]+/g, " ").trim().split(/\s+/);
  const token = words
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
  return token ? token.slice(0, 40) : fallback;
}

/** `{CandidateName}_{Role}_WriteUp.pdf` — matches the backend Content-Disposition. */
function writeupFilename(candidateName: string, role: string | null | undefined): string {
  return `${filenameToken(candidateName, "Candidate")}_${filenameToken(role, "Role")}_WriteUp.pdf`;
}

/** Minimal shape of an export row (from `useExports`) we depend on here. */
interface WriteupExport {
  id: string;
  kind: string;
  scope_id: string | null;
  status: string;
  created_at: string;
}

/**
 * The candidate's most-recent non-expired `candidate_writeup` export, or null.
 * This is the single source of truth the button state derives from.
 */
function useCurrentWriteup(candidateId: string): WriteupExport | null {
  const { data } = useExports();
  return useMemo(() => {
    if (!data) return null;
    const mine = (data as WriteupExport[])
      .filter(
        (e) =>
          e.kind === "candidate_writeup" &&
          e.scope_id === candidateId &&
          e.status !== "expired",
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    return mine[0] ?? null;
  }, [data, candidateId]);
}

/** Modal body: polls the export status and renders the PDF preview. */
function WriteupPollingFlow({
  exportId,
  filename,
  onRegenerate,
}: {
  exportId: string;
  filename: string;
  onRegenerate: () => void;
}): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { data } = useExport(exportId);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSave(): Promise<void> {
    if (!blob) return;
    setSaving(true);
    await saveBlobAsFile(blob, filename);
    setSaving(false);
  }

  if (!data) {
    return <GeneratingState label="Loading…" />;
  }

  if (data.status === READY) {
    return (
      <div className="space-y-3">
        {/* Scrollable PDF preview area */}
        <div className="max-h-[55vh] overflow-y-auto rounded border border-border-default bg-surface-subtle p-2">
          <WriteupPreview
            tenantSlug={tenantSlug}
            exportId={exportId}
            onBlobReady={setBlob}
          />
        </div>
        {/* Save action — enabled once the blob is ready */}
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => void handleSave()}
            disabled={!blob || saving}
            data-testid="writeup-save-btn"
          >
            {saving ? <Spinner size="sm" /> : null}
            Download Write-Up (PDF)
          </Button>
        </div>
      </div>
    );
  }

  if (data.status === "failed") {
    return (
      <button
        type="button"
        onClick={onRegenerate}
        className="text-sm text-status-rejected-fg underline"
      >
        Failed — retry
      </button>
    );
  }

  if (data.status === "expired") {
    return (
      <button
        type="button"
        onClick={onRegenerate}
        className="text-sm text-text-secondary underline"
      >
        Expired — request new
      </button>
    );
  }

  // pending / running — a real activity graphic, plus the close-and-return hint.
  return (
    <GeneratingState label="Generating…" reassure />
  );
}

/**
 * The generating/activity state: an animated spinner + label so it's visibly
 * doing work (not frozen), plus a reassurance line that the modal is safe to
 * close because the job keeps running.
 */
function GeneratingState({
  label,
  reassure = false,
}: {
  label: string;
  reassure?: boolean;
}): JSX.Element {
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 py-6 text-text-secondary"
      data-testid="writeup-generating"
      role="status"
      aria-live="polite"
    >
      <Spinner size="lg" />
      <span className="text-sm font-medium text-text-primary">{label}</span>
      {reassure ? (
        <span className="max-w-xs text-center text-xs text-text-secondary">
          You can close this and keep working — we&apos;ll keep generating and
          notify you when it&apos;s ready to download.
        </span>
      ) : null}
    </div>
  );
}

export function ExportClientWriteupButton({
  candidateId,
  candidateName,
  role,
  className,
}: ExportClientWriteupButtonProps): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const createExport = useCreateExport();
  const addToast = useToastStore((s) => s.addToast);
  const filename = writeupFilename(candidateName, role);

  const current = useCurrentWriteup(candidateId);
  // The export the modal is currently attached to (null → modal closed). This
  // is separate from `current` so CLOSING the modal never cancels the job.
  const [modalExportId, setModalExportId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const status = current?.status ?? null;
  const isGenerating = status !== null && IN_FLIGHT.has(status);
  const isReady = status === READY;

  /** Fetch the already-generated artifact and save it — no regeneration. */
  async function instantDownload(exportId: string): Promise<void> {
    setDownloading(true);
    const res = await downloadExport(tenantSlug, exportId, filename);
    setDownloading(false);
    if (!res.ok) {
      addToast(res.error ?? "Couldn't download the write-up.", "error");
    }
  }

  // ── Completion toast + close-and-return notification ─────────────────────────
  // When a tracked write-up transitions from in-flight → complete, surface a
  // success toast with a one-click Download action. Seeded from `current` on
  // mount WITHOUT toasting, so an already-complete artifact does not fire a
  // stale "ready" toast on page load.
  const prevRef = useRef<{ id: string; status: string } | null>(
    current ? { id: current.id, status: current.status } : null,
  );
  useEffect(() => {
    const prev = prevRef.current;
    if (
      current &&
      prev &&
      prev.id === current.id &&
      IN_FLIGHT.has(prev.status) &&
      current.status === READY
    ) {
      addToast(`Write-up for ${candidateName} is ready.`, "success", {
        label: "Download",
        onClick: () => void instantDownload(current.id),
      });
    }
    prevRef.current = current ? { id: current.id, status: current.status } : null;
    // `instantDownload` / `candidateName` are stable enough for this signal;
    // re-running only on `current` identity is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  /** Start a brand-new generation and open the modal to watch it. */
  function startGeneration(): void {
    createExport.mutate(
      { kind: "candidate_writeup", scope_id: candidateId },
      {
        onSuccess: (d) => setModalExportId(d.id),
        onError: (err) => {
          // 409 → an identical export is already in flight; attach to it so the
          // user watches the existing job instead of getting an error wall.
          const inflight = (
            err as {
              response?: { data?: { detail?: { export_id?: string } } };
            }
          )?.response?.data?.detail?.export_id;
          if (inflight) setModalExportId(inflight);
        },
      },
    );
  }

  /** Primary click — behaviour depends on the current job state. */
  function handleClick(): void {
    if (isReady && current) {
      // Instant re-download of the cached artifact — never regenerate.
      void instantDownload(current.id);
      return;
    }
    if (isGenerating && current) {
      // Re-open the in-flight job to watch progress (close-and-return).
      setModalExportId(current.id);
      return;
    }
    startGeneration();
  }

  const buttonLabel = isGenerating ? "Generating…" : "Download Write-Up (PDF)";
  const compactLabel = isGenerating ? "Generating…" : "Write-Up";
  const busy = createExport.isPending || downloading;

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleClick}
        disabled={busy}
        className={className}
        data-testid="export-client-writeup"
      >
        {busy || isGenerating ? <Spinner size="sm" /> : null}
        {/* Full label on desktop (≥lg); compact below lg so the header's
            right-actions block doesn't crowd the name/meta into wrapping at
            768px (iPad portrait). */}
        <span className="hidden lg:inline">{buttonLabel}</span>
        <span className="lg:hidden">{compactLabel}</span>
      </Button>

      {modalExportId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-h-[85vh] w-[min(48rem,92vw)] space-y-4 overflow-hidden rounded-xl border border-border-default bg-surface-elevated p-5 shadow-lg">
            <h2 className="font-semibold text-text-primary">
              Client write-up for {candidateName}
            </h2>
            <p className="text-sm text-text-secondary">
              A branded, client-ready PDF assessment of{" "}
              <span className="font-medium text-text-primary">{candidateName}</span>
              {role ? <> for {role}</> : null}. Preview it below, then use
              &ldquo;Download Write-Up (PDF)&rdquo; to save a copy.
            </p>
            <WriteupPollingFlow
              exportId={modalExportId}
              filename={filename}
              onRegenerate={startGeneration}
            />
            <div className="flex items-center justify-between border-t border-border-default pt-3">
              <button
                type="button"
                onClick={startGeneration}
                className="text-xs text-text-secondary underline hover:text-text-primary"
                data-testid="writeup-regenerate"
              >
                Regenerate
              </button>
              <button
                type="button"
                onClick={() => setModalExportId(null)}
                className="text-sm text-text-secondary underline hover:text-text-primary"
                data-testid="writeup-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {createExport.isError &&
      (createExport.error as {
        response?: { data?: { detail?: { error?: string } } };
      })?.response?.data?.detail?.error !== "action_already_running" ? (
        <p className="text-xs text-status-rejected-fg" role="alert">
          Couldn&apos;t start the write-up. Try again.
        </p>
      ) : null}
    </>
  );
}
